import { datasheetVariablesQueries } from '@/duckdb/datasheet-variables';
import { modelContributionsQueries } from '@/duckdb/model-contributions';
import { spendsHistoryQueries } from '@/duckdb/spends-history';
import { optimizerService } from '@/optimizer';
import { Prettify } from '@analytical-alley/ui/types';
import moment from 'moment';

export async function getRoiCurves(deltaRoi: { [key: string]: number[] }) {
    const { 'Yearly spend': yearlySpend, ...restDeltaRoi } = deltaRoi;

    const xAxis = yearlySpend ? Object.values(yearlySpend) : [];

    const curves = Object.fromEntries(
        Object.entries(restDeltaRoi).map(([key, value]) => [
            key,
            Object.values(value),
        ]),
    );

    const variablesAndCategories =
        await datasheetVariablesQueries.getVariablesAndCategories(
            Object.keys(restDeltaRoi),
        );

    return {
        curves: curves,
        xAxis: xAxis,
        variablesAndCategories,
    };
}

export type GetPreviousDataReturn = Prettify<
    Awaited<ReturnType<typeof getPreviousData>>
>;

export async function getPreviousData({
    startDate: startDateArg,
    endDate: endDateArg,
    interval,
}: {
    interval: 'daily' | 'weekly';
    startDate?: string;
    endDate?: string;
}) {
    const startDate = startDateArg
        ? moment(startDateArg, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : undefined;
    const endDate = endDateArg
        ? moment(endDateArg, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : undefined;

    const deltaRoi = await optimizerService.getRoiCurvesData();
    const { 'Yearly spend': _, ...restDeltaRoi } = deltaRoi;
    const mediaVariables = Object.keys(restDeltaRoi);

    const [
        totals,
        rows,
        roiCurves,
        mediaContribution,
        contributionsByCategory,
        nonMediaKpi,
    ] = await Promise.all([
        spendsHistoryQueries.getTotals({ interval, startDate, endDate }),
        spendsHistoryQueries.getRows({
            interval,
            period: 'month',
            startDate,
            endDate,
        }),
        getRoiCurves(deltaRoi),
        modelContributionsQueries.getMediaContribution({
            period: interval,
            startDate,
            endDate,
            mediaVariables,
        }),
        modelContributionsQueries.getContributionsByCategory({
            startDate,
            endDate,
        }),
        optimizerService.getNonMediaKpi(),
    ]);
    const totalContribution = mediaContribution + nonMediaKpi;

    return {
        totals,
        rows,
        roiCurves,
        totalContribution,
        mediaContribution,
        contributionsByCategory,
    };
}
